import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { unscopedKey } from "@/lib/queryClient";
import { Button } from "@/components/ui/Button";
import type { KratosFlow } from "@/lib/kratos";
import {
  createFlow,
  firstFlowError,
  KratosError,
  KratosFlowExpiredError,
  kratosErrorCopy,
  nodeMessages,
  submitFlow,
} from "@/lib/kratos";
import { exchangeKratosSession } from "@/features/auth/kratos/exchange";

/**
 * In-app AAL2 step-up (#358): a Kratos login flow created with
 * `aal=aal2` against the EXISTING first-factor session. Kratos renders
 * only second-factor methods (TOTP); completing it upgrades the Kratos
 * session to `aal2`, and the session exchange mirrors the new
 * `amr`/`acr` onto the app session so `session_satisfied_mfa()` passes
 * and the privileged action that 403'd can be retried.
 *
 * Two non-form outcomes matter:
 *   - Kratos answers `session_already_available` — the Kratos session
 *     ALREADY satisfies aal2 (e.g. TOTP was enrolled this session,
 *     which upgrades it in place) and only the app session is stale.
 *     Skip the form: re-exchange and continue.
 *   - No Kratos session at all — the only path is a fresh sign-in.
 *
 * Reached via `/login?aal=aal2&next=…` — the target the `apiFetch`
 * 403 `mfa_required` handler redirects to.
 */
export function KratosStepUpPanel({ nextPath }: { nextPath: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [flow, setFlow] = useState<KratosFlow | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dead, setDead] = useState(false);

  const finishStepUp = useCallback(async () => {
    // Re-exchange: the Kratos session now carries aal2 + totp; the
    // backend re-mints the app session with the upgraded amr/acr.
    await exchangeKratosSession();
    await queryClient.invalidateQueries();
    await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
    void navigate({ to: nextPath, replace: true });
  }, [navigate, nextPath, queryClient]);

  const initialize = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setFlow(await createFlow("login", { aal: "aal2" }));
    } catch (err) {
      if (isSessionAlreadySatisfied(err)) {
        // The Kratos session already meets aal2 — only the app
        // session is stale. No second factor to collect.
        try {
          await finishStepUp();
          return;
        } catch (exchangeErr) {
          setError(
            exchangeErr instanceof Error
              ? exchangeErr.message
              : "Could not refresh your session.",
          );
        }
      } else {
        setError(kratosErrorCopy(err));
        setDead(true);
      }
    } finally {
      setLoading(false);
    }
  }, [finishStepUp]);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !flow) return;
    setSubmitting(true);
    setError(null);
    try {
      let outcome;
      try {
        outcome = await submitFlow("login", flow, {
          method: "totp",
          totp_code: code.trim(),
        });
      } catch (err) {
        if (!(err instanceof KratosFlowExpiredError)) throw err;
        const fresh = await createFlow("login", { aal: "aal2" });
        setFlow(fresh);
        outcome = await submitFlow("login", fresh, {
          method: "totp",
          totp_code: code.trim(),
        });
      }
      if (outcome.kind === "success") {
        await finishStepUp();
        return;
      }
      setFlow(outcome.flow);
    } catch (err) {
      setError(kratosErrorCopy(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <p data-testid="step-up-loading" className="font-mono text-xs text-ink-muted">
        Preparing verification...
      </p>
    );
  }

  if (!flow) {
    // No aal2 flow and not already satisfied: the first-factor Kratos
    // session is gone (expired, signed out). A fresh sign-in lands
    // back here via `next` if step-up is still required.
    return (
      <div
        data-testid="step-up-no-session"
        className="flex flex-col gap-2 rounded border border-rule bg-elevated p-3"
      >
        <p className="text-sm text-ink">
          {dead && error ? error : "Your sign-in session has ended."}
        </p>
        <Link
          to="/login"
          search={{ next: nextPath }}
          className="font-mono text-xs text-accent hover:underline"
        >
          Sign in again
        </Link>
      </div>
    );
  }

  const flowError = firstFlowError(flow);
  const codeMessages = nodeMessages(flow, "totp_code");

  return (
    <form
      data-testid="step-up-form"
      className="flex flex-col gap-3"
      onSubmit={handleSubmit}
    >
      <p className="flex items-center gap-2 text-sm text-ink">
        <ShieldCheck size={16} strokeWidth={2.2} aria-hidden="true" />
        This action needs a second factor. Enter the 6-digit code from
        your authenticator app.
      </p>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Authenticator code
        </span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          autoFocus
          data-testid="step-up-totp-code"
          className="rounded border border-rule bg-canvas px-3 py-2 font-mono text-sm tracking-[0.25em] text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </label>
      {codeMessages.map((message) => (
        <p key={message} className="font-mono text-xs text-destructive">
          {message}
        </p>
      ))}
      {flowError ? (
        <p data-testid="step-up-error" className="font-mono text-xs text-destructive">
          {flowError}
        </p>
      ) : null}
      {error ? (
        <p data-testid="step-up-exchange-error" className="font-mono text-xs text-destructive">
          {error}
        </p>
      ) : null}
      <p className="font-mono text-[0.65rem] leading-relaxed text-ink-muted">
        No authenticator enrolled yet? Add one under Account → Security,
        then retry the action.
      </p>
      <Button
        type="submit"
        disabled={submitting || !code.trim()}
        data-testid="step-up-submit"
      >
        {submitting ? "Verifying..." : "Verify"}
      </Button>
    </form>
  );
}

function isSessionAlreadySatisfied(err: unknown): boolean {
  if (!(err instanceof KratosError)) return false;
  const id = (err.body as { error?: { id?: string } } | null)?.error?.id ?? "";
  return id === "session_already_available";
}
